import { useEffect, useRef, useState } from "react";
import { Button, Card, PageHeader } from "@/components/ui";
import { useAssistant, useInstall } from "@/lib/api";

export function InstallScreen() {
  const install = useInstall();
  const [copied, setCopied] = useState(false);
  const snippet = install.data?.snippet ?? "";
  return (
    <>
      <PageHeader
        title="Install"
        subtitle="Paste this before the closing </body> tag of every page. That is the whole installation."
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="flex min-w-0 flex-col gap-4">
          <pre className="overflow-x-auto rounded-control bg-ink p-4 text-[12px] leading-relaxed text-white">
            <code>{snippet || "…"}</code>
          </pre>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              disabled={!snippet}
              onClick={() => {
                void navigator.clipboard
                  .writeText(snippet)
                  .then(() => setCopied(true));
              }}
            >
              Copy snippet
            </Button>
            {copied ? (
              <span className="text-[13px] text-success">Copied.</span>
            ) : null}
          </div>
          <p className="text-[13px] text-muted">
            Works on any site: WordPress, Webflow, Shopify, Squarespace, or
            hand-written HTML. Tag managers work too. Your key is{" "}
            <code className="rounded bg-surface-2 px-1">
              {install.data?.publicKey ?? "…"}
            </code>
            ; it is safe to be public.
          </p>
        </Card>
        <WidgetPreview />
      </div>
    </>
  );
}

/**
 * The real widget bundle, mounted in an iframe so its shadow host sits on
 * its own page — exactly what a customer's site sees, including the fixed
 * positioning. Reloads when the config changes.
 */
export function WidgetPreview() {
  const install = useInstall();
  const config = useAssistant();
  const ref = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const frame = ref.current;
    if (!frame || !install.data) return;
    const html = `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:system-ui;color:#94a3b8;display:flex;align-items:center;justify-content:center;height:100vh}</style></head><body><p>Your website</p>${install.data.snippet}</body></html>`;
    frame.srcdoc = html;
  }, [install.data, config.dataUpdatedAt]);
  return (
    <Card className="p-0">
      <header className="border-b border-border px-5 py-3">
        <p className="font-semibold">Preview</p>
        <p className="text-[12px] text-muted">
          The real widget, as visitors see it.
        </p>
      </header>
      <iframe
        ref={ref}
        title="Widget preview"
        className="h-[560px] w-full rounded-b-panel bg-bg"
        sandbox="allow-scripts allow-same-origin"
      />
    </Card>
  );
}
